import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { getLanguage } from '@/features/language/api';
import { APP_CONST } from '@/shared/constants';
import type { LOCALES } from '@/shared/i18n/locales';

export type Locale = typeof LOCALES;

interface LocaleContextProps {
  locale: keyof Locale;
  setLocale: (locale: keyof Locale) => void;
}

const LocaleContext = createContext<LocaleContextProps>({
  locale: 'ru',
  setLocale: () => {}
});

interface LocaleProviderProps {
  children: React.ReactNode;
}

export const LocaleProvider = ({ children }: LocaleProviderProps) => {
  const [locale, setLocale] = useState<keyof Locale>(
    (JSON.parse(
      localStorage.getItem(APP_CONST.LOCAL_STORAGE.LOCALE)!
    ) as keyof Locale) || 'ru'
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    const checkLanguage = async () =>
      queryClient.fetchQuery({
        queryKey: ['initialLanguage'],
        queryFn: () => getLanguage({})
      });
    if (!localStorage.getItem(APP_CONST.LOCAL_STORAGE.LOCALE)) {
      setLocale('ru');
      localStorage.setItem(
        APP_CONST.LOCAL_STORAGE.LOCALE,
        JSON.stringify('ru')
      );
    }

    if (
      !localStorage.getItem(APP_CONST.LOCAL_STORAGE.LOCALE) &&
      localStorage.getItem(APP_CONST.LOCAL_STORAGE.AUTH_TOKEN)
    ) {
      checkLanguage().then((response) => {
        if (response.data.culture) {
          setLocale(response.data?.culture as keyof Locale);
          localStorage.setItem(
            APP_CONST.LOCAL_STORAGE.LOCALE,
            JSON.stringify(response.data?.culture)
          );
        }
      });
    }
  }, []);

  const value = useMemo(
    () => ({
      locale,
      setLocale: (locale: keyof Locale) => {
        localStorage.setItem(
          APP_CONST.LOCAL_STORAGE.LOCALE,
          JSON.stringify(locale)
        );

        setLocale(locale);
      }
    }),
    [locale]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export const useLocaleContext = () => useContext(LocaleContext);
